import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';
import { useToast } from '../components/Toast/ToastContext';
import * as apiPoints from './apiPoints';
import { markNodeSeenNow } from './useUnread';
import { errorMessage } from './api';
import AdoptedBlock from './AdoptedBlock';
import PointRow, { type RowPoint } from './PointRow';
import { canManage } from './permissions';
import type { Adoption } from './pointsTypes';
import type { PlanMember, PlanRole } from './types';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

// Imports from DocSection are used only inside render, never in a module-level
// constant: DocSection imports this file, so at module-evaluation time they are
// still undefined.

const ADD_KEY = `${buttonClass('tonal', 'md')} flex-shrink-0`;

const GLIDE_MS = 200;
const EMPHASIZED = 'cubic-bezier(0.2, 0, 0, 1)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The adoption moment: the line starts where the point's row was and settles into
 * the adopted block, then its gold wash fades. Reduced motion keeps the wash and
 * drops the travel. Web Animations, so it runs on the compositor and a second
 * adoption mid-flight simply starts over.
 */
const land = (line: HTMLElement, from: DOMRect | null) => {
  if (typeof line.animate !== 'function') return;
  const reduce = prefersReducedMotion();
  if (from && !reduce) {
    const to = line.getBoundingClientRect();
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    if (dx || dy) {
      line.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
        duration: GLIDE_MS,
        easing: EMPHASIZED,
      });
    }
  }
  const wash = line.querySelector<HTMLElement>('[data-land]');
  wash?.animate?.([{ opacity: 1 }, { opacity: 1, offset: reduce ? 0.4 : 0.3 }, { opacity: 0 }], {
    duration: 900,
    easing: 'ease-out',
  });
};

const fullName = (p: RowPoint) => `${p.author?.firstname || ''} ${p.author?.lastname || ''}`.trim() || 'A former member';

let pendingSeq = 0;

export interface PointCounts {
  points: number;
  votes: number;
}

export interface PointsPanelProps {
  planId: string;
  nodeId: string;
  role: PlanRole | null;
  userId: string | null;
  /** The node's authority: adopts, unadopts and lifts vetoes like a manager, on this node only. */
  authorityId: string | null;
  /** Locked freezes points and adoption; votes and discussion stay open. */
  locked: boolean;
  /** Archived freezes everything. */
  archived?: boolean;
  /** Plan members, offered for @mentions in threads. */
  members?: PlanMember[];
  /**
   * The node's change counter from the plan. Every point, vote, adoption and reply
   * bumps it, so a new value after a live reload means someone else wrote here.
   */
  seq?: number;
  /** `panel` adds a heading and pins the add box to the bottom of the scrolling side panel. */
  variant?: 'section' | 'panel';
  /** After any write here lands, so the page can refresh counts and unread. */
  onChanged?: () => void;
  /** The counts this panel sees, whenever its list changes. */
  onCounts?: (counts: PointCounts) => void;
}

const PointsPanel: React.FC<PointsPanelProps> = ({
  planId,
  nodeId,
  role,
  userId,
  authorityId,
  locked,
  archived = false,
  members = [],
  seq,
  variant = 'section',
  onChanged,
  onCounts,
}) => {
  const toast = useToast();
  const [points, setPoints] = useState<RowPoint[] | null>(null);
  const [adoptions, setAdoptions] = useState<Adoption[]>([]);
  const [pending, setPending] = useState<RowPoint[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [drafting, setDrafting] = useState(false);
  const latest = useRef(0);
  const voting = useRef(new Set<string>());
  const lines = useRef(new Map<string, HTMLElement>());
  const glide = useRef<{ pointId: string; rect: DOMRect | null } | null>(null);
  const onCountsRef = useRef(onCounts);
  onCountsRef.current = onCounts;

  const contributor = !!role && role !== 'viewer';
  const canWrite = contributor && !locked && !archived;
  const canVote = contributor && !archived;
  const canDiscuss = contributor && !archived;
  const holdsNode = !!userId && authorityId === userId;
  const canAdopt = canWrite && (canManage(role) || holdsNode);

  const reload = useCallback(async () => {
    const req = ++latest.current;
    try {
      const [p, a] = await Promise.all([apiPoints.listPoints(planId, nodeId), apiPoints.listAdoptions(planId, nodeId)]);
      if (req !== latest.current) return;
      setPoints(p?.points ?? []);
      setAdoptions(a?.adoptions ?? []);
      setLoadError(null);
    } catch (e) {
      if (req !== latest.current) return;
      setLoadError(errorMessage(e, 'Could not load the notes for this topic.'));
    }
  }, [planId, nodeId]);

  useEffect(() => {
    setPoints(null);
    setAdoptions([]);
    setPending([]);
    setLoadError(null);
    reload();
    return () => {
      latest.current += 1;
    };
  }, [reload]);

  const firstSeq = useRef(true);
  useEffect(() => {
    if (firstSeq.current) {
      firstSeq.current = false;
      return;
    }
    reload();
  }, [seq, reload]);

  useEffect(() => {
    if (!points) return;
    onCountsRef.current?.({ points: points.length, votes: points.reduce((n, p) => n + (p.votes || 0), 0) });
  }, [points]);

  useLayoutEffect(() => {
    const g = glide.current;
    if (!g) return;
    const line = lines.current.get(g.pointId);
    if (!line) return;
    glide.current = null;
    land(line, g.rect);
  }, [adoptions]);

  const registerLine = useCallback((pointId: string, el: HTMLElement | null) => {
    if (el) lines.current.set(pointId, el);
    else lines.current.delete(pointId);
  }, []);

  /** After an own write: the fresh list, a read mark so the change is not unread to its author, then the page. */
  const settle = async () => {
    await reload();
    await Promise.resolve()
      .then(() => markNodeSeenNow(planId, nodeId))
      .catch(() => undefined);
    onChanged?.();
  };

  const fail = (e: unknown, fallback: string) => {
    toast.error(errorMessage(e, fallback));
    reload();
  };

  const vote = async (point: RowPoint) => {
    if (voting.current.has(point.id)) return;
    voting.current.add(point.id);
    const set = (voted: boolean, votes: number) =>
      setPoints((prev) => prev && prev.map((p) => (p.id === point.id ? { ...p, voted, votes } : p)));
    set(!point.voted, Math.max(0, point.votes + (point.voted ? -1 : 1)));
    try {
      const res = await apiPoints.votePoint(planId, nodeId, point.id);
      if (res) set(res.voted, res.votes);
      await Promise.resolve()
        .then(() => markNodeSeenNow(planId, nodeId))
        .catch(() => undefined);
      onChanged?.();
    } catch (e) {
      set(point.voted, point.votes);
      toast.error(errorMessage(e, 'Could not record your vote. Try again.'));
    } finally {
      voting.current.delete(point.id);
    }
  };

  const add = async () => {
    const text = draft.trim();
    if (!text || !userId) return;
    pendingSeq += 1;
    const temp: RowPoint = {
      id: `pending-${pendingSeq}`,
      node_id: nodeId,
      user_id: userId,
      author: { firstname: null, lastname: null },
      body: text,
      sort: Number.MAX_SAFE_INTEGER,
      edited_at: null,
      created_at: new Date().toISOString(),
      votes: 0,
      voted: false,
      revisions: 0,
      adopted: false,
      pending: true,
    };
    setPending((p) => [...p, temp]);
    setDraft('');
    try {
      await apiPoints.createPoint(planId, nodeId, text);
      await settle();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not add your note. Try again.'));
      // Hand the words back rather than losing them, unless something new was typed meanwhile.
      setDraft((d) => d || text);
    } finally {
      setPending((p) => p.filter((x) => x.id !== temp.id));
    }
  };

  const edit = async (point: RowPoint, body: string) => {
    try {
      await apiPoints.updatePoint(planId, nodeId, point.id, body);
      await settle();
      return true;
    } catch (e) {
      fail(e, 'Could not save your note.');
      return false;
    }
  };

  const remove = async (point: RowPoint) => {
    try {
      await apiPoints.deletePoint(planId, nodeId, point.id);
      await settle();
      toast.success('Note deleted');
      return true;
    } catch (e) {
      fail(e, 'Could not delete your note.');
      return false;
    }
  };

  const adopt = async (point: RowPoint, row: HTMLElement | null) => {
    glide.current = { pointId: point.id, rect: row ? row.getBoundingClientRect() : null };
    try {
      await apiPoints.adoptPoint(planId, nodeId, point.id);
      await settle();
    } catch (e) {
      glide.current = null;
      fail(e, 'Could not adopt the note.');
    }
  };

  const unadopt = async (pointId: string) => {
    try {
      await apiPoints.unadoptPoint(planId, nodeId, pointId);
      await settle();
    } catch (e) {
      fail(e, 'Could not unadopt the note.');
    }
  };

  const threadWrote = () => {
    Promise.resolve()
      .then(() => markNodeSeenNow(planId, nodeId))
      .catch(() => undefined)
      .then(() => onChanged?.());
  };

  const pointsById = useMemo(() => new Map((points ?? []).map((p) => [p.id, p])), [points]);

  // The server orders the owner first, then authors by name; that order is kept.
  // Your own branch goes last, right above the box that adds to it.
  const { others, mine } = useMemo(() => {
    const groups = new Map<string, RowPoint[]>();
    const own: RowPoint[] = [];
    for (const p of points ?? []) {
      if (userId && p.user_id === userId) {
        own.push(p);
        continue;
      }
      const g = groups.get(p.user_id);
      if (g) g.push(p);
      else groups.set(p.user_id, [p]);
    }
    return { others: [...groups.entries()], mine: [...own, ...pending] };
  }, [points, pending, userId]);

  const rowProps = {
    planId,
    nodeId,
    role,
    userId,
    authorityId,
    members,
    canVote,
    canWrite,
    canAdopt,
    canDiscuss,
    onVote: vote,
    onAdopt: adopt,
    onEdit: edit,
    onDelete: remove,
    onThreadWrote: threadWrote,
    refresh: seq,
  };

  // Your branch is headed by your name, the way every other branch is by its author's.
  const me = members.find((m) => m.user_id === userId);
  const saved = mine.find((p) => !p.pending);
  const ownName = (me ? `${me.firstname || ''} ${me.lastname || ''}`.trim() : '') || `${saved?.author?.firstname || ''} ${saved?.author?.lastname || ''}`.trim();
  const ownLabel = ownName ? `${ownName} (you)` : 'You';

  const total = points?.length ?? 0;
  const isEmpty = points !== null && total === 0 && pending.length === 0;
  const pinned = variant === 'panel';

  return (
    <section aria-label="Notes" className="min-w-0">
      {variant === 'panel' && (
        <h3 className="mb-1.5 flex items-baseline gap-2 text-sm font-semibold text-on-surface">
          Notes
          <Appear show={points !== null && total > 0} className="text-xs font-normal tabular-nums text-on-surface-variant">
            {total}
          </Appear>
        </h3>
      )}

      <FadeIn key={loadError ? 'error' : points === null ? 'loading' : 'ready'}>
        {loadError ? (
          <Alert variant="note" onRetry={() => reload()}>{loadError}</Alert>
        ) : points === null ? (
          <div aria-busy="true">
            <SkeletonGroup label="Loading notes" className="space-y-2 py-1">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-start gap-2">
                  <Skeleton className="h-8 w-11 flex-shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1.5 pt-1.5">
                    <Skeleton className="h-3 w-4/5 rounded" />
                    <Skeleton className="h-3 w-2/5 rounded" />
                  </div>
                </div>
              ))}
            </SkeletonGroup>
          </div>
        ) : (
          <div className="space-y-3">
            <AdoptedBlock
              adoptions={adoptions}
              pointsById={pointsById}
              canUnadopt={canAdopt}
              onUnadopt={unadopt}
              registerLine={registerLine}
            />

            <Reveal show={isEmpty}>
              <EmptyState variant="line">
                {canWrite ? 'No notes yet. Add the first one below.' : 'No notes on this topic yet.'}
              </EmptyState>
            </Reveal>

            {others.map(([authorId, list]) => (
              <div key={authorId}>
                <div className="truncate pb-0.5 pl-12 text-xs font-semibold text-on-surface-variant" title={fullName(list[0])}>
                  {fullName(list[0])}
                </div>
                <ul className="space-y-0.5">
                  {list.map((p) => (
                    <PointRow key={p.id} point={p} isOwn={false} {...rowProps} />
                  ))}
                </ul>
              </div>
            ))}

            {mine.length > 0 && (
              <div>
                <div className="truncate pb-0.5 pl-12 text-xs font-semibold text-on-surface-variant" title={ownLabel}>
                  {ownLabel}
                </div>
                <ul className="space-y-0.5">
                  {mine.map((p) => (
                    <PointRow key={p.id} point={p} isOwn {...rowProps} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </FadeIn>

      {canWrite && userId && points !== null && !loadError ? (
        <div className={pinned ? 'sticky bottom-0 z-10 -mx-4 mt-3 bg-surface-container px-4 pb-2 pt-2' : 'mt-3'}>
          <div className="flex items-end gap-1.5 pl-12">
            <TextArea
              value={draft}
              rows={1}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={() => setDrafting(true)}
              onBlur={() => setDrafting(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  add();
                }
              }}
              aria-label="Add your note"
              placeholder="Add your note"
              maxLength={4000}
              className="min-h-[36px]"
            />
            <Tooltip label="Add this note under your name">
              <button type="button" onClick={add} disabled={!draft.trim()} className={ADD_KEY}>
                Add
              </button>
            </Tooltip>
          </div>
          <Reveal show={drafting}>
            <p className="mt-1 hidden pl-12 text-[11px] text-on-surface-variant [@media(hover:hover)]:block">
              Enter adds it, Shift+Enter starts a new line.
            </p>
          </Reveal>
        </div>
      ) : (
        contributor &&
        locked &&
        !archived && (
          <p className="mt-3 pl-12 text-[11px] text-on-surface-variant">
            This plan is locked, so notes cannot be added or changed. Votes and replies stay open.
          </p>
        )
      )}
    </section>
  );
};

export default PointsPanel;
